import React, { useState, useImperativeHandle, forwardRef } from 'react';
import MultiSelectTicker from '../MultiSelectTicker';
import ScreenerSavedParams from '../ScreenerSavedParams';
import { BookmarkPlus, PlusCircle, Play } from 'lucide-react';
import {
  basicStrategies,
  formatTickersForAPI,
} from '../../utils/presetMappings';
import '../../styles/MobileScreenerForm.css';

const strategyOrder = [
  'short-itm-call', 'short-otm-call',
  'medium-itm-call', 'medium-otm-call',
  'long-itm-call', 'long-otm-call',
];

const findMatchingStrategy = (scenario) => {
  const isPut = scenario.option_type === 'put';
  const normalizedGain = Math.abs(scenario.stock_gain_pct);
  const normalizedStrike = isPut && scenario.strike_pct !== 0 ? -scenario.strike_pct : scenario.strike_pct;
  for (const [key, s] of Object.entries(basicStrategies)) {
    if (
      Math.abs(s.days_until_exp - scenario.days_until_exp) <= 5 &&
      Math.abs(s.strike_pct - normalizedStrike) < 0.01 &&
      Math.abs(s.days_to_gain - scenario.days_to_gain) <= 2 &&
      Math.abs(s.stock_gain_pct - normalizedGain) < 0.01
    ) {
      return key;
    }
  }
  return null;
};

const MobileScreenerBasicForm = forwardRef(({
  onAddToQueue,
  onAddAndRun,
  onSaveScenario,
  queueCount,
  loading,
  savedParams = [],
  onLoadParam,
  onDeleteParam,
  onAddParamToQueue,
}, ref) => {
  const [selectedTickers, setSelectedTickers] = useState([]);
  const [direction, setDirection] = useState(null);  // 'call' | 'put'
  const [selectedStrategies, setSelectedStrategies] = useState([]);
  const [isPresetSheetOpen, setIsPresetSheetOpen] = useState(false);

  const isPut = direction === 'put';

  const isFormValid = () =>
    selectedTickers.length > 0 &&
    direction !== null &&
    selectedStrategies.length > 0;

  const buildScenarios = () => {
    if (!isFormValid()) return [];
    const tickerValues = formatTickersForAPI(selectedTickers);
    return selectedStrategies.map(strategyKey => {
      const s = basicStrategies[strategyKey];
      const adjustedGain = isPut ? -Math.abs(s.stock_gain_pct) : Math.abs(s.stock_gain_pct);
      const adjustedStrike = isPut && s.strike_pct !== 0 ? -s.strike_pct : s.strike_pct;
      const adjustedLabel = isPut ? s.label.replace('Bull', 'Bear') : s.label;
      return {
        tickers: tickerValues,
        option_type: direction,
        days_until_exp: s.days_until_exp,
        strike_pct: adjustedStrike,
        days_to_gain: s.days_to_gain,
        stock_gain_pct: adjustedGain,
        allocation: s.allocation,
        _exitPercent: s.days_to_gain / s.days_until_exp,
        label: `${tickerValues.join(',')} ${isPut ? 'Puts' : 'Calls'} - ${adjustedLabel}`,
      };
    });
  };

  useImperativeHandle(ref, () => ({
    getFormState: () => ({
      selectedTickers,
      direction,
      selectedStrategies,
      isValid: isFormValid(),
    }),
    buildScenarios,
    isFormValid,
    resetForm: () => {
      setSelectedTickers([]);
      setDirection(null);
      setSelectedStrategies([]);
    },
    loadScenario: (scenario) => {
      const tickerArray = Array.isArray(scenario.tickers)
        ? scenario.tickers
        : (scenario.tickers || '').split(',').map(t => t.trim()).filter(Boolean);
      setSelectedTickers(tickerArray.map(t => ({ value: t, label: t })));
      setDirection(scenario.option_type);
      const matched = findMatchingStrategy(scenario);
      setSelectedStrategies(matched ? [matched] : []);
    },
  }));

  const toggleStrategy = (key) => {
    setSelectedStrategies(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleAddToQueue = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddToQueue) {
      scenarios.forEach(s => onAddToQueue(s));
    }
  };

  const handleRunNow = () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onAddAndRun) {
      onAddAndRun(scenarios);
    }
  };

  const handleSave = async () => {
    const scenarios = buildScenarios();
    if (scenarios.length > 0 && onSaveScenario) {
      for (const s of scenarios) {
        const ok = await onSaveScenario(s);
        if (!ok) break;
      }
    }
  };

  // Adjust pill labels for puts (mirror desktop behavior: ITM/OTM character
  // preserved, physical strike-pct sign flips).
  const getDisplayedStrike = (strategy) => {
    if (!isPut || strategy.strike_pct === 0) return strategy.display.strike;
    if (strategy.strike_pct < 0) return 'ITM +25%';
    return 'OTM -25%';
  };

  const getAdjustedLabel = (label) => (isPut ? label.replace('Bull', 'Bear') : label);

  return (
    <div className="msf-root">
      <div className="msf-form-header">
        <button
          type="button"
          className="msf-load-preset-btn"
          onClick={() => setIsPresetSheetOpen(true)}
        >
          <BookmarkPlus className="msf-load-preset-btn-icon" />
          Load preset
        </button>
      </div>

      {/* Tickers */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Tickers</h3>
          <span className="msf-section-count">
            {selectedTickers.length > 0 ? `${selectedTickers.length} selected` : 'None selected'}
          </span>
        </div>
        <div className="msf-ticker-input-wrap">
          <MultiSelectTicker
            value={selectedTickers}
            onChange={(tickers) => setSelectedTickers(tickers || [])}
            placeholder="Add ticker..."
          />
        </div>
      </div>

      {/* Direction (Basic mode = 2 cards) */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Market direction</h3>
        </div>
        <div className="msf-direction-grid">
          <button
            type="button"
            onClick={() => setDirection('call')}
            className={`msf-direction-card ${direction === 'call' ? 'msf-selected' : ''}`}
          >
            <span className="msf-direction-title">
              <span className="msf-direction-radio" />
              Bullish
            </span>
            <span className="msf-direction-subtitle">Buy calls</span>
          </button>
          <button
            type="button"
            onClick={() => setDirection('put')}
            className={`msf-direction-card ${direction === 'put' ? 'msf-selected' : ''}`}
          >
            <span className="msf-direction-title">
              <span className="msf-direction-radio" />
              Bearish
            </span>
            <span className="msf-direction-subtitle">Buy puts</span>
          </button>
        </div>
      </div>

      {/* Strategy */}
      <div className="msf-section">
        <div className="msf-section-header">
          <h3 className="msf-section-title">Strategy</h3>
          <div className="msf-section-meta">
            <span className="msf-section-count">
              {selectedStrategies.length > 0 ? `${selectedStrategies.length} selected` : 'None selected'}
            </span>
            {selectedStrategies.length > 0 && (
              <button type="button" className="msf-section-clear" onClick={() => setSelectedStrategies([])}>
                Clear
              </button>
            )}
          </div>
        </div>
        <div className="msf-pill-row-wrap">
          <div className="msf-pill-row">
            {strategyOrder.map(key => {
              const s = basicStrategies[key];
              if (!s) return null;
              const isSelected = selectedStrategies.includes(key);
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => toggleStrategy(key)}
                  className={`msf-pill msf-pill-strategy ${isSelected ? 'msf-selected' : ''}`}
                  title={s.tooltip}
                >
                  <span className="msf-pill-label">{getAdjustedLabel(s.label)}</span>
                  <span className="msf-pill-sublabel">
                    {s.display.expiration} · {getDisplayedStrike(s)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA stack: Run / Add to queue / Save as preset */}
      <div className="msf-cta-wrap">
        <button
          type="button"
          className="msf-cta"
          onClick={handleRunNow}
          disabled={!isFormValid() || loading}
        >
          <Play className="msf-cta-icon" style={{ fill: 'currentColor' }} />
          {selectedStrategies.length > 1
            ? `Run ${selectedStrategies.length} scenarios`
            : 'Run scenario'}
        </button>
        <button
          type="button"
          className="msf-cta msf-cta-secondary"
          onClick={handleAddToQueue}
          disabled={!isFormValid() || loading}
        >
          <PlusCircle className="msf-cta-icon" />
          {selectedStrategies.length > 1
            ? `Add ${selectedStrategies.length} to queue`
            : 'Add to queue'}
          {queueCount > 0 && <span className="msf-cta-badge">{queueCount} queued</span>}
        </button>
        <button
          type="button"
          className="msf-save-btn"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
        >
          Save as preset
        </button>
        <p className="msf-cta-hint">
          Tap any pill to toggle. The count in each section header updates as you select;
          "Clear" wipes the section in one tap.
        </p>
      </div>

      {/* Load preset slide-up sheet */}
      <div
        className={`msf-sheet-backdrop ${isPresetSheetOpen ? 'msf-sheet-open' : ''}`}
        onClick={() => setIsPresetSheetOpen(false)}
      />
      <div className={`msf-sheet ${isPresetSheetOpen ? 'msf-sheet-open' : ''}`}>
        <div className="msf-sheet-handle-bar" />
        <div className="msf-sheet-header">
          <span className="msf-sheet-title">Saved presets</span>
          <button
            type="button"
            className="msf-sheet-close"
            onClick={() => setIsPresetSheetOpen(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="msf-sheet-body">
          <ScreenerSavedParams
            savedParams={savedParams}
            onLoad={(p) => { if (onLoadParam) onLoadParam(p); setIsPresetSheetOpen(false); }}
            onDelete={onDeleteParam}
            onAddToQueue={(p) => { if (onAddParamToQueue) onAddParamToQueue(p); setIsPresetSheetOpen(false); }}
            compact
          />
        </div>
      </div>
    </div>
  );
});

export default MobileScreenerBasicForm;
